import api from "../../../../utils/api";
import { useMemo, useState } from "react";
import { useCreateAdPlan } from "./useCreateAdPlan";
import { useAppSelector } from "../../../../redux/hooks";
import adPlanTemplateUrl from "/template.xlsx?url";
import { CSVReader } from "react-papaparse";
import { Button, buttonRecipe } from "../../basic/Button";
import { captureException } from "@sentry/react";
import { Row, Col } from "react-bootstrap";
import { GOALS, AD_TYPES, AD_STATES, TARGET_TYPES, ALGORITHM_OPTIONS, BID_STRATEGY_OPTIONS } from "./constants";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import { Separator } from "react-aria-components";
import { FaFileExcel } from "react-icons/fa6";
import { Center, Flex, HStack } from "../../../../../styled-system/jsx";
import { getCampaignsByAdType } from "../../../../utils/getCampaignsByAdType";
import { Modal } from "../../basic/Modal";
import Loading from "../Loading";
import { css } from "../../../../../styled-system/css";
import { successToast } from "../../../../utils/toasts";
import { useTranslation } from "react-i18next";
import { parseNumericCell } from "../../../../utils/formatNumber";
import {
  getKeywordError,
  isValidAsin,
  useKeywordErrorMessage,
} from "../../../../modules/advertising/utils/keywordValidation";

const Label = ({ children, style }) => (
  <div className="mt-3 mb-2" style={{ fontWeight: "bold", fontSize: "14px", ...style }}>
    <span className="text-nowrap">{children}</span>
  </div>
);

const MULTIPLIER_MESSAGES = {
  topOfSearchBidMultiplier: ($) => $.advertising['Row {row}: Top of search must be between 0 and 900 (got "{value}")'],
  restOfSearchMultiplier: ($) => $.advertising['Row {row}: Rest of search must be between 0 and 900 (got "{value}")'],
  productPageMultiplier: ($) =>
    $.advertising['Row {row}: Product bid multiplier must be between 0 and 900 (got "{value}")'],
  amazonBusinessMultiplier: ($) =>
    $.advertising['Row {row}: Amazon business (all placements) multiplier must be between 0 and 900 (got "{value}")'],
};

const MULTIPLIER_MIN = 0;
const MULTIPLIER_MAX = 900;

const getAsinError = (asin) => (isValidAsin(asin) ? null : "asin");

const UPLOADABLE_AD_GOALS = [GOALS.DEMAND_GENERATION, GOALS.PRODUCT_LAUNCH, GOALS.OPTIMIZE_ACOS];

const LIST_CELL_SEPARATORS = /[\n,]/;
const splitListCell = (value) =>
  value
    ?.split(LIST_CELL_SEPARATORS)
    .map((item) => item.trim())
    .filter(Boolean) ?? [];

const Value = ({ children }) => (
  <div className="mb-2 fs-standard">
    <span className="text-nowrap">{children}</span>
  </div>
);

// Flatten the productid response (parents with nested children) into a flat
// list of { value, sku, asin } used for ASIN matching.
const flattenProducts = (data) =>
  data.flatMap((product) => {
    const rows = [{ isDisabled: product.is_parent, value: product.id, sku: `${product.sku}`, asin: product.asin }];
    if (product.is_parent) {
      for (const child of product.children) {
        rows.push({ value: child.id, sku: `${child.sku} (${child.asin})`, asin: child.asin });
      }
    }
    return rows;
  });

//no rows -> no data
const AdPlanBulkCreate = (props) => {
  const { t } = useTranslation(["advertising", "common"]);
  const keywordErrorMessage = useKeywordErrorMessage();
  const targetErrorMessage = (code) =>
    code === "asin" ? t(($) => $.advertising["ASINs must be 10 letters and/or numbers."]) : keywordErrorMessage(code);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState("upload");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRow, setSelectedRow] = useState(0);
  const createAdPlan = useCreateAdPlan();

  const { data: categories } = useCategoriesQuery(marketPlace, props.channel);

  const handleClose = () => {
    props.onClose();
    setRows([]);
    setError("");
    setPage("upload");
  };

  const handleOnRemoveFile = () => {
    setRows([]);
    setError("");
  };

  const handleSaveAdPlans = async () => {
    for (const row of rows) {
      const adPlan = {
        ...row,
        goal: row.adGoal.value,
        adType: row.adType.value,
        advStatus: AD_STATES.ACTIVE.value,
        product: row.product.map((p) => p.value),
        bidStrategy: row.bidStrategyType.value,
        campaignNames: getCampaignsByAdType(row.adType.value, {}, row.autoCampaignEnabled),
        manualCampaignBudget: 70,
        autoCampaignBudget: 30,
      };

      try {
        await createAdPlan.mutateAsync({ adPlan, notify: false });
      } catch {
        return;
      }
    }
    successToast({
      message: t(($) => $.advertising["Successfully Created Ads"]),
    });
    setPage("upload");
    handleClose();
  };

  const handleOnDrop = async (data) => {
    setIsProcessing(true);
    try {
      await processDroppedData(data);
    } finally {
      setIsProcessing(false);
    }
  };

  const processDroppedData = async (data) => {
    // onDrop meaining on drag and drop file in
    const processedData = [];
    const newErrors = [];
    //find header row to map the column indexes
    const headers_col_map = {
      name: "name",
      "ad strategy": "adGoal",
      "optimal acos range low": "targetAcos",
      "optimal acos range high": "maxAcos",
      "daily budget": "dailyBudget",
      "ad type": "adType",
      category: "category",
      "products: asins (separate multiple products by new line)": "productASINSKU",
      "target type": "targetType",
      algorithm: "algorithm",
      "bid strategy": "bidStrategy",
      "auto campaign enabled": "autoCampaignEnabled",
      "keyword harvesting enabled": "keywordHarvestingEnabled",
      "keyword discovery enabled": "keywordDiscoveryEnabled",
      "asin harvesting enabled": "asinHarvestingEnabled",
      "trellis trak enabled": "trellisTrakEnabled",
      "min bid": "minBid",
      "max bid": "maxBid",
      "top of search": "topOfSearchBidMultiplier",
      "rest of search": "restOfSearchMultiplier",
      "amazon business multiplier": "amazonBusinessMultiplier",
      "product bid multiplier": "productPageMultiplier",
      "target keywords": "filledTags",
      "target asins": "filledAsins",
      "product type": "filledProductKeys",
      brands: "filledProductBrands",
      attributes: "attributes",
      "negative keywords": "filledNegativeKeywords",
      "negative asins": "filledNegativeAsins",
      "match types": "matchTypes",
    };

    const col_map = data[0].data.reduce((acc, h, idx) => {
      const header = h.trim().toLowerCase();
      if (Object.keys(headers_col_map).includes(header)) {
        acc[headers_col_map[header]] = idx;
      }
      return acc;
    }, {});

    //check if we found our column headers
    const headers = data[0].data.map((h) => h.toLowerCase().trim());

    const missing_cols = Object.keys(headers_col_map).filter((col) => !headers.includes(col));
    if (missing_cols.length > 0) {
      newErrors.push(
        t(($) => $.advertising["Row 1: has incorrect header(s). Missing columns: {columns}"], {
          columns: missing_cols.join(", "),
        }),
      );
      setError(newErrors);
    } else {
      let row_idx = 1;
      const num_columns = 8;

      //process the rows
      for (row_idx; row_idx < data.length; row_idx++) {
        const row = data[row_idx];
        if (row.data.length <= 0 || row.data[0]?.trim() === "") {
          continue;
        } else if (row.data.length < num_columns - 2) {
          newErrors.push(t(($) => $.advertising["Row {row}: has missing values"], { row: row_idx + 1 }));
          continue;
        }
        const name = row.data[col_map["name"]].trim();
        const adGoal = row.data[col_map["adGoal"]].trim().toLowerCase();
        let filledTags = row.data[col_map["filledTags"]].trim();
        const targetACOS = row.data[col_map["targetAcos"]].trim();
        const maxACOS = row.data[col_map["maxAcos"]].trim();
        const dailyBudget = row.data[col_map["dailyBudget"]].trim();
        const adType = row.data[col_map["adType"]].trim().toLowerCase();
        const category = row.data[col_map["category"]].trim();
        const productASINSKU = row.data[col_map["productASINSKU"]].trim();
        const targetType = row.data[col_map["targetType"]]?.trim()?.toLowerCase();
        let matchTypes = row.data[col_map["matchTypes"]].trim()?.toLowerCase();
        const algorithm = row.data[col_map["algorithm"]].trim();
        const keywordHarvestingEnabled = row.data[col_map["keywordHarvestingEnabled"]].trim().toLowerCase() === "true";
        const keywordDiscoveryEnabled = row.data[col_map["keywordDiscoveryEnabled"]].trim().toLowerCase() === "true";
        const asinHarvestingEnabled = row.data[col_map["asinHarvestingEnabled"]].trim().toLowerCase() === "true";
        const trellisTrakEnabled = row.data[col_map["trellisTrakEnabled"]].trim().toLowerCase() === "true";
        const minBid = parseNumericCell(row.data[col_map["minBid"]]);
        const maxBid = parseNumericCell(row.data[col_map["maxBid"]]);
        const topOfSearchBidMultiplier = row.data[col_map["topOfSearchBidMultiplier"]].trim();
        const restOfSearchMultiplier = row.data[col_map["restOfSearchMultiplier"]].trim();
        const amazonBusinessMultiplier = row.data[col_map["amazonBusinessMultiplier"]].trim();
        const productPageMultiplier = row.data[col_map["productPageMultiplier"]].trim();
        const bidStrategy = row.data[col_map["bidStrategy"]].trim();
        let filledAsins =
          row.data[col_map["filledAsins"]] === undefined ? null : row.data[col_map["filledAsins"]]?.trim();
        let filledNegativeKeywords =
          row.data[col_map["filledNegativeKeywords"]] === undefined ?
            null
          : row.data[col_map["filledNegativeKeywords"]]?.trim();

        let filledNegativeAsins =
          row.data[col_map["filledNegativeAsins"]] === undefined ?
            null
          : row.data[col_map["filledNegativeAsins"]]?.trim();
        matchTypes = row.data[col_map["matchTypes"]] === undefined ? null : row.data[col_map["matchTypes"]]?.trim();
        let broadMatchEnabled = false;
        let phraseMatchEnabled = false;
        let exactMatchEnabled = false;
        let filledProductBrands = [];
        let filledProductKeys = [];
        let filteredAttributes = [];

        const channel = "AMZ";
        const uploadGoalLabel = (goal) => t(goal.label, { lng: "en" }).toLowerCase();
        const matchAdGoal = UPLOADABLE_AD_GOALS.find((goal) => uploadGoalLabel(goal) === adGoal);
        if (!matchAdGoal) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: Ad Strategy must be one of: {options}"], {
              row: row_idx + 1,
              options: UPLOADABLE_AD_GOALS.map(uploadGoalLabel).join(", "),
            }),
          );
        }
        filledTags = splitListCell(filledTags);
        filledAsins = splitListCell(filledAsins).map((asin) => asin.toUpperCase());
        filledNegativeKeywords = splitListCell(filledNegativeKeywords);
        filledNegativeAsins = splitListCell(filledNegativeAsins).map((asin) => asin.toUpperCase());

        const rowNumber = row_idx + 1;
        const checkTargets = (values, getError, message) => {
          for (const value of values) {
            const code = getError(value);
            if (code) {
              newErrors.push(t(message, { row: rowNumber, value, reason: targetErrorMessage(code) }));
            }
          }
        };
        checkTargets(
          filledTags,
          (kw) => getKeywordError(kw, { allowLeadingPlus: channel === "AMZ" }),
          ($) => $.advertising['Row {row}: invalid keyword "{value}". {reason}'],
        );
        checkTargets(
          filledNegativeKeywords,
          getKeywordError,
          ($) => $.advertising['Row {row}: invalid negative keyword "{value}". {reason}'],
        );
        checkTargets(filledAsins, getAsinError, ($) => $.advertising['Row {row}: invalid ASIN "{value}". {reason}']);
        checkTargets(
          filledNegativeAsins,
          getAsinError,
          ($) => $.advertising['Row {row}: invalid negative ASIN "{value}". {reason}'],
        );

        // Match types
        matchTypes = splitListCell(matchTypes?.toLowerCase());
        if (matchTypes.includes("broad")) {
          broadMatchEnabled = true;
        }
        if (matchTypes.includes("phrase")) {
          phraseMatchEnabled = true;
        }
        if (matchTypes.includes("exact")) {
          exactMatchEnabled = true;
        }

        const targetACOSValue = parseNumericCell(targetACOS);
        const maxACOSValue = parseNumericCell(maxACOS);
        const dailyBudgetValue = parseNumericCell(dailyBudget);

        const rawMultipliers = {
          topOfSearchBidMultiplier,
          restOfSearchMultiplier,
          productPageMultiplier,
          amazonBusinessMultiplier,
        };
        const multipliers = {};
        for (const [field, message] of Object.entries(MULTIPLIER_MESSAGES)) {
          const value = parseNumericCell(rawMultipliers[field]);
          if (value === null || value < MULTIPLIER_MIN || value > MULTIPLIER_MAX) {
            newErrors.push(t(message, { row: row_idx + 1, value: rawMultipliers[field] }));
          }
          multipliers[field] = value;
        }

        if (targetACOSValue === null || targetACOSValue <= 0) {
          newErrors.push(
            t(
              ($) => $.advertising['Row {row}: Optimal ACOS Range Low must be a number greater than 0 (got "{value}")'],
              {
                row: row_idx + 1,
                value: targetACOS,
              },
            ),
          );
        }
        if (maxACOSValue === null || maxACOSValue <= targetACOSValue) {
          newErrors.push(
            t(
              ($) =>
                $.advertising[
                  'Row {row}: Optimal ACOS Range High must be a number greater than Target ACOS (got "{value}")'
                ],
              { row: row_idx + 1, value: maxACOS },
            ),
          );
        }
        if (dailyBudgetValue === null || dailyBudgetValue < 1) {
          newErrors.push(
            t(($) => $.advertising['Row {row}: Daily Budget must be a number greater than 1 (got "{value}")'], {
              row: row_idx + 1,
              value: dailyBudget,
            }),
          );
        }

        const matchAdType = [AD_TYPES.SPONSORED_PRODUCT, AD_TYPES.SPONSORED_PRODUCT_AUTO].find(
          (type) => t(type.label, { lng: "en" }).toLowerCase() === adType,
        );
        if (!matchAdType) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: Ad Type must be 'sponsored product' or 'sponsored product: auto'"], {
              row: row_idx + 1,
            }),
          );
        }

        const algorithmType = [
          ALGORITHM_OPTIONS.PRODUCT_LAUNCH,
          ALGORITHM_OPTIONS.GROWTH_MAX,
          ALGORITHM_OPTIONS.ROAS_BOOST,
        ].find((option) => t(option.label, { lng: "en" }).toLowerCase() === algorithm.toLowerCase().trim());
        if (!algorithmType) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: Algorithm must be {options}"], {
              row: row_idx + 1,
              options: Object.values(ALGORITHM_OPTIONS)
                .map((option) => t(option.label).toLowerCase())
                .join(", "),
            }),
          );
        }
        const bidStrategyType = [
          BID_STRATEGY_OPTIONS.DOWN_ONLY,
          BID_STRATEGY_OPTIONS.FIXED,
          BID_STRATEGY_OPTIONS.UP_DOWN,
        ].find((strategy) => t(strategy.label, { lng: "en" }).toLowerCase() === bidStrategy.toLowerCase());
        if (!bidStrategyType) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: Bid strategy must be 'up / down', 'fixed', or 'down only'"], {
              row: row_idx + 1,
            }),
          );
        }

        const matchAdTarget = [TARGET_TYPES.AUTOMATIC, TARGET_TYPES.MANUAL, TARGET_TYPES.FUNNEL].find(
          (target) => target.value.toLowerCase() === targetType.toLowerCase(),
        );
        if (!matchAdTarget) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: Target Type must be 'automatic', 'manual' or 'funnel'"], {
              row: row_idx + 1,
            }),
          );
        }

        const cleanKw = (val) =>
          val ?
            (val
              .split("\n")
              .map((kw) => kw.trim())
              .filter((kw) => !!kw) ?? [])
          : [];

        const funnelKeywords = {};
        if (matchAdTarget?.value === TARGET_TYPES.FUNNEL.value) {
          filledProductBrands = cleanKw(row.data[col_map["filledProductBrands"]]);
          filledProductKeys = cleanKw(row.data[col_map["filledProductKeys"]]);
          filteredAttributes = {
            other: cleanKw(row.data[col_map["attributes"]]),
          };
        }

        const matchCategory = categories.find(
          (cat) => cat?.leaf_category_name?.toLowerCase() === category?.toLowerCase(),
        );
        if (!matchCategory) {
          newErrors.push(t(($) => $.advertising["Row {row}: Invalid Category name"], { row: row_idx + 1 }));
        }

        const rowAsins = splitListCell(productASINSKU).map((p) => p.slice(0, 10).toUpperCase());
        const productArray = [];
        const printedProductArray = [];
        if (!rowAsins.length) {
          newErrors.push(
            t(($) => $.advertising["Row {row}: at least one product ASIN is required"], { row: row_idx + 1 }),
          );
        }
        const productData = rowAsins.length ? await fetchProducts(rowAsins) : [];
        for (const productASIN of rowAsins) {
          const products = productData.filter((p) => p.asin === productASIN);
          if (!products.length) {
            newErrors.push(
              t(($) => $.advertising['Row {row}: product "{value}" was not found in this marketplace'], {
                row: row_idx + 1,
                value: productASIN,
              }),
            );
          } else {
            productArray.push(...products);
            printedProductArray.push(productASIN);
          }
        }

        if (name == null || name.length > 200 || name.length < 1) {
          newErrors.push(t(($) => $.advertising["Row {row}: Invalid name length"], { row: row_idx + 1 }));
        }

        processedData.push({
          name,
          adGoal: matchAdGoal,
          filledTags,
          targetACOS: targetACOSValue,
          maxACOS: maxACOSValue,
          dailyBudget: dailyBudgetValue,
          adType: matchAdType,
          algorithm: algorithmType?.value,
          category: matchCategory?.id,
          categoryName: matchCategory?.leaf_category_name,
          categoryId: matchCategory?.id,
          productASINSKU,
          product: productArray,
          printedProductArray,
          targetType,
          matchAdTarget,
          keywordHarvestingEnabled,
          keywordDiscoveryEnabled,
          asinHarvestingEnabled,
          trellisTrakEnabled,
          minBid,
          maxBid,
          ...multipliers,
          bidStrategy,
          bidStrategyType,
          channel,
          filledAsins,
          funnelKeywords,
          filledNegativeKeywords,
          filledNegativeAsins,
          broadMatchEnabled,
          exactMatchEnabled,
          phraseMatchEnabled,
          filledProductBrands,
          filledProductKeys,
          filteredAttributes,
        });
      }

      setRows(processedData);
      setError(newErrors);
      setPage("preview");
    }
  };

  // Without the ASIN scope the endpoint returns the whole catalog and times out.
  const fetchProducts = async (asins) => {
    try {
      const params = { parent: true, marketplace: marketPlace, asins: asins.join(",") };
      const res = await api.get("/api/gvads/productid/", params);
      return res.data ? flattenProducts(res.data) : [];
    } catch (e) {
      captureException(e);
      return [];
    }
  };
  const rowItem = useMemo(() => {
    if (rows.length > 0) {
      return rows[selectedRow];
    }
    return {};
  }, [rows, selectedRow]);
  return (
    <Modal
      isOpen={props.show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={
        error.length > 0 && !isProcessing ?
          t(($) => $.common.modalAlert.Error)
        : t(($) => $.advertising["Import Media Plans"])
      }
      css={{ width: "60vw", padding: "xlarge" }}
    >
      {isProcessing ?
        <Flex justifyContent={"center"} marginX={"auto"} marginY={"auto"}>
          <Loading fullPage={false} marginY={"auto"} height={"200px"} width={"350px"} />
        </Flex>
      : error.length > 0 ?
        <>
          <hr />
          <div className={css({ maxHeight: "50vh", overflowY: "auto", width: "100%" })}>
            {error.map((e, idx) => (
              <p
                key={`${idx}-${e}`}
                style={{
                  fontWeight: "400",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {e}
              </p>
            ))}
          </div>
          <hr />
          <Center>
            <Button onPress={handleClose}>{t(($) => $.common.actions.Close)}</Button>
          </Center>
        </>
      : page === "upload" ?
        <>
          <Row style={{ content: "center" }}>
            <CSVReader
              onDrop={handleOnDrop}
              addRemoveButton
              removeButtonColor="#659cef"
              onRemoveFile={handleOnRemoveFile}
              style={{
                dropArea: {
                  margin: "1rem",
                  marginLeft: "auto",
                  marginRight: "auto",
                  borderColor: "pink",
                  borderRadius: 20,
                  backgroundColor: "rgba(162, 176, 175, 0.1)",
                  height: 75,
                },
              }}
            >
              <span style={{ fontSize: 15 }}>
                {t(($) => $.advertising["Drop CSV file here or click to bulk upload."])}
              </span>
            </CSVReader>
            <br></br>
          </Row>
          <Row className="text-center">
            <a href={adPlanTemplateUrl} download className={buttonRecipe()}>
              <FaFileExcel /> {t(($) => $.advertising["Download Template"])}
            </a>
          </Row>
        </>
      : <>
          <hr />
          <Row style={{ overflow: "scroll" }}>
            <Col xs={12} style={{ overflow: "auto" }}>
              <Row>
                <Col xs={12}>
                  <Label style={{ fontSize: "1.8rem", textAlign: "center" }}>
                    {t(($) => $.advertising["Preview ({current} of {total})"], {
                      current: selectedRow + 1,
                      total: rows.length,
                    })}
                  </Label>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <h3>{t(($) => $.advertising["Setup"])}</h3>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Name:"])}</Label>
                  <Value>{rowItem.name}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Ad Type"])}</Label>
                  <Value>{rowItem.adType?.label ? t(rowItem.adType.label) : ""}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Ad Strategy:"])}</Label>
                  <Value>{rowItem.adGoal?.label ? t(rowItem.adGoal.label) : ""}</Value>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Optimal ACOS Range Low:"])}</Label>
                  <Value>{rowItem.targetACOS}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Optimal ACOS Range High:"])}</Label>
                  <Value>{rowItem.maxACOS}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Daily Budget:"])}</Label>
                  <Value>{rowItem.dailyBudget}</Value>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Label>{t(($) => $.advertising["Category:"])}</Label>
                  <Value>{rowItem.categoryName}</Value>
                </Col>
              </Row>
              <Separator />
              <Row>
                <Col xs={12}>
                  <h3>{t(($) => $.advertising["Ad Products"])}</h3>
                </Col>
                {rowItem.product.map((prod) => (
                  <Col xs={4} key={prod.id}>
                    {prod.asin}/{prod.sku}
                  </Col>
                ))}
              </Row>
              <Separator />
              <Row>
                <Col xs={12}>
                  <h3>{t(($) => $.advertising["Targeting"])}</h3>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Label>{t(($) => $.advertising["Target Type:"])}</Label>
                  <Value>{rowItem.matchAdTarget?.label ? t(rowItem.matchAdTarget.label) : ""}</Value>
                </Col>
              </Row>
              {rowItem.matchAdTarget.value === TARGET_TYPES.FUNNEL.value && (
                <Row>
                  <Col xs={4}>
                    <Label>{t(($) => $.advertising["Product Type:"])}</Label>
                    <Value>{rowItem.filledProductKeys?.join(", ")}</Value>
                  </Col>
                  <Col xs={4}>
                    <Label>{t(($) => $.advertising["Brands:"])}</Label>
                    <Value>{rowItem.filledProductBrands?.join(", ")}</Value>
                  </Col>
                  <Col xs={4}>
                    <Label>{t(($) => $.advertising["Other Attributes:"])}</Label>
                    <Value>{rowItem.filteredAttributes?.other?.join(", ")}</Value>
                  </Col>
                </Row>
              )}
              <Row>
                <Col xs={12}>
                  <Label>{t(($) => $.advertising["Targeted Keywords:"])}</Label>
                  <Value>{rowItem.filledTags.join(", ")}</Value>
                </Col>
                <Col xs={12}>
                  <Label>{t(($) => $.advertising["Targeted ASINs:"])}</Label>
                  <Value>{rowItem.filledAsins.join(", ")}</Value>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Label>{t(($) => $.advertising["Negative Keywords:"])}</Label>
                  <Value>{rowItem.filledNegativeKeywords.join(", ")}</Value>
                </Col>
                <Col xs={6}>
                  <Label>{t(($) => $.advertising["Negative ASINs:"])}</Label>
                  <Value>{rowItem.filledNegativeAsins.join(", ")}</Value>
                </Col>
              </Row>
              <Separator />
              <Row>
                <Col xs={12}>
                  <h3>{t(($) => $.advertising["Advanced Options"])}</h3>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Algorithm:"])}</Label>
                  <Value>{rowItem.algorithm}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Min Bid:"])}</Label>
                  <Value>{rowItem.minBid}</Value>
                </Col>
                <Col xs={4}>
                  <Label>{t(($) => $.advertising["Max Bid:"])}</Label>
                  <Value>{rowItem.maxBid}</Value>
                </Col>
              </Row>
              <Row>
                <Col xs={6}>
                  <Row>
                    <Col xs={12}>
                      <h4>{t(($) => $.advertising["Harvesting"])}</h4>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Keyword Harvesting:"])}</Label>
                      <Value>{rowItem.keywordHarvestingEnabled ? t(($) => $.common.Yes) : t(($) => $.common.No)}</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Keyword Discovery:"])}</Label>
                      <Value>{rowItem.keywordDiscoveryEnabled ? t(($) => $.common.Yes) : t(($) => $.common.No)}</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["ASIN Harvesting:"])}</Label>
                      <Value>{rowItem.asinHarvestingEnabled ? t(($) => $.common.Yes) : t(($) => $.common.No)}</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Trellis Trak:"])}</Label>
                      <Value>{rowItem.trellisTrakEnabled ? t(($) => $.common.Yes) : t(($) => $.common.No)}</Value>
                    </Col>
                  </Row>
                </Col>
                <Col xs={6}>
                  <Row>
                    <Col xs={12}>
                      <h4>{t(($) => $.advertising["Amazon Settings"])}</h4>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Top of Search Bid Multiplier:"])}</Label>
                      <Value>{rowItem.topOfSearchBidMultiplier}%</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Bid Strategy:"])}</Label>
                      <Value>{rowItem.bidStrategyType?.label ? t(rowItem.bidStrategyType.label) : ""}</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Rest of Search Bid Multiplier:"])}</Label>
                      <Value>{rowItem.restOfSearchMultiplier}%</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Product Bid Multiplier:"])}</Label>
                      <Value>{rowItem.productPageMultiplier}%</Value>
                    </Col>
                    <Col xs={12}>
                      <Label>{t(($) => $.advertising["Amazon Business (All Placements) Multiplier:"])}</Label>
                      <Value>{rowItem.amazonBusinessMultiplier}%</Value>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <HStack justifyContent={"center"}>
            <Button variant="secondary" onPress={() => setSelectedRow(selectedRow - 1)} isDisabled={selectedRow === 0}>
              <HiOutlineChevronLeft />
            </Button>
            <Button
              variant="secondary"
              onPress={() => setSelectedRow(selectedRow + 1)}
              isDisabled={selectedRow === rows.length - 1}
            >
              <HiOutlineChevronRight />
            </Button>
          </HStack>
          <hr />
          <HStack justifyContent={"space-around"}>
            <Button variant="secondary" onPress={handleClose}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button onClick={handleSaveAdPlans}>{t(($) => $.common.actions.Save)}</Button>
          </HStack>

          {createAdPlan.error?.response?.data?.error && <Row>{createAdPlan.error.response.data.error}</Row>}
        </>
      }
    </Modal>
  );
};

export default AdPlanBulkCreate;
